import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { RefObject } from 'react';
import {
  Box,
  Group,
  Stack,
  Text,
  UnstyledButton,
  Paper,
  ScrollArea,
  TextInput,
  Textarea,
  ActionIcon,
  Tooltip,
  Center,
  Badge,
  rem,
  Indicator,
  Button,
  SimpleGrid,
  Loader,
  Popover,
  Skeleton,
  Modal,
  Radio,
  RadioGroup,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconFileDescription,
  IconKeyboard,
  IconMicrophone,
  IconMoodSmile,
  IconPaperclip,
  IconPhoto,
  IconSend2,
  IconPhoneCall,
  IconVideo,
  IconPalette,
  IconChevronLeft,
  IconDots,
  IconArrowDown,
  IconUserX,
  IconFlag,
  IconPlus,
  IconInfoCircle,
} from '@tabler/icons-react';
import { submitReport, REPORT_REASON_LABELS, type ReportReason } from '@domain/safety/report';
import type { CallBootstrap, CallConnectionState, CallSession, CallType } from '@domain/call/types';
import type { ActiveRealtimeSession } from '../hooks/useRealtimeCollaboration';
import { ImageSourcePicker } from '@shared/ui/ImageSourcePicker';
import { AppIcon } from '@shared/ui/AppIcon';
import type { EmojiComposerHandle } from '../../../emoji/EmojiComposer';
import { EmojiComposer } from '../../../emoji/EmojiComposer';
import { EmojiPicker } from '../../../emoji/EmojiPicker';
import { EmojiText } from '../../../emoji/EmojiText';
import { AudioMessageBubble } from '../components/AudioMessageBubble';
import { CallMessageCard } from '../components/CallMessageCard';
import { isCallMessageType } from '@domain/call/message';
import type { ChatMessage, ChatSession, DirectoryMap, GroupMap } from '@domain/chat/types';
import {
  fileMessageStatusLabel,
  formatFileBytes,
  realtimeFileTransferStatusLabel,
} from '@domain/file/fileTransferPresentation';
import type { FileTransferTask } from '@domain/file/types';
import type { FileTransferLifecycleSnapshot } from '@domain/file/api';
import type { FileOperationCapability } from '@domain/file/capability';
import { UserAvatar } from '@shared/ui/UserAvatar';
import { extractImagePayload, imageAspectRatio, imageBubbleWidth, isImageMessageEnvelope, resolveImageUrl } from '@shared/utils/ui';
import { isAudioMsgType } from '@shared/utils/audio';
import { WORKSPACE_MOBILE_QUERY } from '../hooks/useAdaptiveWorkspaceLayout';
import { resolveSessionAvatarUrl } from '../services/sessionAvatarPresentation';
import {
  formatChatTimelineTime,
  shouldShowChatSenderNickname,
  shouldShowChatTimeSeparator,
} from '../services/chatTimelinePresentation';

type ConversationStageProps = {
  activeSession: ChatSession | null;
  activeGroup: GroupMap[number] | undefined;
  activeMessages: ChatMessage[];
  currentUserId: number;
  cdnBase: string;
  contactsById: DirectoryMap;
  callBootstrap: CallBootstrap | null;
  callConnectionState: CallConnectionState;
  activeCall: CallSession | null;
  isOnline: boolean;
  connState: 'idle' | 'connecting' | 'online' | 'error';
  composerText: string;
  setComposerText: React.Dispatch<React.SetStateAction<string>>;
  isAudioMode: boolean;
  setIsAudioMode: React.Dispatch<React.SetStateAction<boolean>>;
  isRecordingAudio: boolean;
  recordingLabel: string;
  recordingLevels: number[];
  composerRef: RefObject<EmojiComposerHandle | null>;
  feedEnd: RefObject<HTMLDivElement | null>;
  emojiToggleRef: RefObject<HTMLButtonElement | null>;
  isEmojiPickerOpen: boolean;
  setIsEmojiPickerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  loadHistory: (sessionKey: string, mode?: 'initial' | 'older' | 'newer') => Promise<void>;
  isAwaitingInitialHistory: boolean;
  handleStartAudioRecording: () => Promise<void>;
  handleStopAudioRecording: (cancelled: boolean) => Promise<void>;
  handleOpenGroupDetail: () => void;
  handleStartCall: (type: CallType) => void;
  realtimeConnectionState: 'idle' | 'connecting' | 'connected' | 'error';
  activeRealtimeSession: ActiveRealtimeSession | null;
  handleStartRealtimeAudioRoom: () => void;
  handleStartRealtimeVideoRoom: () => void;
  handleStartRealtimeWhiteboard: () => void;
  handleComposerKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => void;
  handleChatImageSelect: (files: File[]) => void;
  handleChatFileSelect: (file: File) => Promise<void>;
  handleRealtimeFileSelect: (file: File) => Promise<void>;
  fileOperationCapability: FileOperationCapability;
  realtimeFileStarting: boolean;
  realtimeFileNotice: string | null;
  handleAcceptFileTask: (assetId: string, fileName?: string) => Promise<void>;
  handleAcceptRealtimeFile: (
    taskId: string,
    expected: { fileName: string; fileSize: number; sha256?: string },
  ) => Promise<void>;
  activeFileTransfers: FileTransferTask[];
  fileTransferLifecycleById: Record<string, FileTransferLifecycleSnapshot | null | false>;
  handleSendMessage: (overrideText?: string) => Promise<void>;
  handleBlockUser: (userId: number, name: string) => Promise<void>;
  blockingUserId: number | null;
  apiBase?: string;
  onBackToList: () => void;
  groupMemberSummary: (
    group: GroupMap[number] | undefined,
    contactsById: DirectoryMap,
    currentUserId: number,
  ) => string;
  setPreviewImageUrl: React.Dispatch<React.SetStateAction<string | null>>;
  onViewUserProfile?: (nameOrId: string | number) => void;
};

function senderLabel(message: ChatMessage, currentUserId: number, contactsById: DirectoryMap) {
  if (message.from === currentUserId) {
    return '我';
  }
  return contactsById[message.from]?.name || `#${message.from}`;
}

function InitialMessagesSkeleton() {
  const rows = [
    { dir: 'in', width: '46%', lines: ['72%', '42%'] },
    { dir: 'out', width: '54%', lines: ['78%', '50%'] },
    { dir: 'in', width: '38%', lines: ['64%'] },
    { dir: 'out', width: '48%', lines: ['70%', '36%'] },
  ] as const;

  return (
    <Box
      h="100%"
      px="md"
      py="lg"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
      }}
    >
      <Stack gap={14}>
        <Center>
          <Skeleton h={18} w={92} radius="xl" opacity={0.32} />
        </Center>
        {rows.map((row, index) => (
          <Group
            key={`${row.dir}-${index}`}
            justify={row.dir === 'out' ? 'flex-end' : 'flex-start'}
            align="flex-start"
            wrap="nowrap"
            gap="sm"
          >
            {row.dir === 'in' && <Skeleton circle w={34} h={34} opacity={0.36} />}
            <Paper
              p="sm"
              radius={8}
              withBorder={false}
              style={{
                width: row.width,
                maxWidth: 280,
                backgroundColor: row.dir === 'out' ? 'rgba(196, 90, 60, 0.16)' : 'rgba(255, 255, 255, 0.72)',
              }}
            >
              <Stack gap={7}>
                {row.lines.map((width, lineIndex) => (
                  <Skeleton key={`${width}-${lineIndex}`} h={8} w={width} radius="xl" opacity={0.42} />
                ))}
              </Stack>
            </Paper>
          </Group>
        ))}
      </Stack>
    </Box>
  );
}

export function ConversationStage({
  activeSession,
  activeGroup,
  activeMessages,
  currentUserId,
  cdnBase,
  contactsById,
  callBootstrap,
  callConnectionState,
  activeCall,
  isOnline,
  connState,
  composerText,
  setComposerText,
  isAudioMode,
  setIsAudioMode,
  isRecordingAudio,
  recordingLabel,
  recordingLevels,
  composerRef,
  feedEnd,
  emojiToggleRef,
  isEmojiPickerOpen,
  setIsEmojiPickerOpen,
  loadHistory,
  isAwaitingInitialHistory,
  handleStartAudioRecording,
  handleStopAudioRecording,
  handleOpenGroupDetail,
  handleStartCall,
  realtimeConnectionState,
  activeRealtimeSession,
  handleStartRealtimeAudioRoom,
  handleStartRealtimeVideoRoom,
  handleStartRealtimeWhiteboard,
  handleComposerKeyDown,
  handleChatImageSelect,
  handleChatFileSelect,
  handleRealtimeFileSelect,
  fileOperationCapability,
  realtimeFileStarting,
  realtimeFileNotice,
  handleAcceptFileTask,
  handleAcceptRealtimeFile,
  activeFileTransfers,
  fileTransferLifecycleById,
  handleSendMessage,
  handleBlockUser,
  blockingUserId,
  apiBase,
  onBackToList,
  groupMemberSummary,
  setPreviewImageUrl,
  onViewUserProfile,
}: ConversationStageProps) {
  const isMobileViewport = useMediaQuery(WORKSPACE_MOBILE_QUERY, false, { getInitialValueInEffect: false });
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState<ReportReason>('harassment');
  const [reportDetail, setReportDetail] = useState('');
  const [reportSubmitting, setReportSubmitting] = useState(false);
  const transferById = useMemo(() => new Map(
    activeFileTransfers.map((task) => [task.taskId, task]),
  ), [activeFileTransfers]);

  const openReportDialog = () => {
    setReportReason('harassment');
    setReportDetail('');
    setReportOpen(true);
  };

  const handleSubmitReport = async () => {
    if (!activeSession) return;
    setReportSubmitting(true);
    try {
      await submitReport('user', String(activeSession.sessionId), reportReason, reportDetail || undefined, apiBase);
      setReportOpen(false);
      notifications.show({
        title: '举报已提交',
        message: '我们会在 24 小时内处理，感谢你的反馈。',
        color: 'green',
        autoClose: 5000,
      });
    } catch {
      notifications.show({
        title: '举报失败',
        message: '提交遇到问题，请稍后重试。',
        color: 'red',
        autoClose: 4000,
      });
    } finally {
      setReportSubmitting(false);
    }
  };
  const isInitialHistoryLoading =
    isAwaitingInitialHistory
    || (
      activeMessages.length === 0
      && !!activeSession
      && (activeSession.historyState === 'idle' || activeSession.historyState === 'loading')
    );

  const audioPressingRef = useRef(false);
  const audioTriggerRef = useRef<HTMLButtonElement | null>(null);
  const messageStageRef = useRef<HTMLElement | null>(null);
  const messageTimelineRef = useRef<HTMLDivElement | null>(null);
  const previousSessionKeyRef = useRef<string | null>(null);
  const previousLatestMessageRef = useRef<string | null>(null);
  const shouldStickToBottomRef = useRef(true);
  const latestUnseenMessageRef = useRef<string | null>(null);
  const pendingInitialRevealRef = useRef(false);
  const initialRevealFrameRef = useRef<number | null>(null);
  const initialLoadingDelayTimerRef = useRef<number | null>(null);
  const initialLoadingHideTimerRef = useRef<number | null>(null);
  const loadingVisibleSinceRef = useRef<number | null>(null);
  const historyRestoreRef = useRef<{
    sessionKey: string;
    scrollHeight: number;
    scrollTop: number;
  } | null>(null);
  const [isTimelineReady, setIsTimelineReady] = useState(true);
  const [showInitialLoadingIndicator, setShowInitialLoadingIndicator] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const [isToolsPanelOpen, setIsToolsPanelOpen] = useState(false);

  const scrollStageToBottom = (behavior: ScrollBehavior = 'auto') => {
    const stage = messageStageRef.current;
    if (!stage) {
      return;
    }

    stage.scrollTo({
      top: stage.scrollHeight,
      behavior,
    });
    shouldStickToBottomRef.current = true;
    latestUnseenMessageRef.current = null;
    setShowJumpToLatest(false);
  };

  const dismissComposerPanels = () => {
    setIsToolsPanelOpen(false);
    setIsEmojiPickerOpen(false);
  };

  const submitComposerMessage = () => {
    if (!composerText.trim()) return;
    dismissComposerPanels();
    void handleSendMessage();
  };

  const handleComposerInputKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && composerText.trim()) {
      dismissComposerPanels();
    }
    handleComposerKeyDown(event);
  };

  const scheduleInitialReveal = () => {
    if (initialRevealFrameRef.current != null) {
      window.cancelAnimationFrame(initialRevealFrameRef.current);
    }

    const step = () => {
      scrollStageToBottom('auto');
      initialRevealFrameRef.current = window.requestAnimationFrame(() => {
        scrollStageToBottom('auto');
        pendingInitialRevealRef.current = false;
        setIsTimelineReady(true);
        initialRevealFrameRef.current = null;
      });
    };

    initialRevealFrameRef.current = window.requestAnimationFrame(step);
  };

  useEffect(() => {
    if (!isAudioMode) {
      audioPressingRef.current = false;
    }
  }, [isAudioMode]);

  useEffect(() => () => {
    if (initialRevealFrameRef.current != null) {
      window.cancelAnimationFrame(initialRevealFrameRef.current);
    }
    if (initialLoadingDelayTimerRef.current != null) {
      window.clearTimeout(initialLoadingDelayTimerRef.current);
    }
    if (initialLoadingHideTimerRef.current != null) {
      window.clearTimeout(initialLoadingHideTimerRef.current);
    }
  }, []);

  useEffect(() => {
    const loadingDelayMs = 360;
    const loadingMinVisibleMs = 320;

    if (initialLoadingDelayTimerRef.current != null) {
      window.clearTimeout(initialLoadingDelayTimerRef.current);
      initialLoadingDelayTimerRef.current = null;
    }
    if (initialLoadingHideTimerRef.current != null) {
      window.clearTimeout(initialLoadingHideTimerRef.current);
      initialLoadingHideTimerRef.current = null;
    }

    if (isInitialHistoryLoading) {
      if (showInitialLoadingIndicator) {
        return;
      }
      initialLoadingDelayTimerRef.current = window.setTimeout(() => {
        loadingVisibleSinceRef.current = Date.now();
        setShowInitialLoadingIndicator(true);
        initialLoadingDelayTimerRef.current = null;
      }, loadingDelayMs);
      return;
    }

    if (!showInitialLoadingIndicator) {
      loadingVisibleSinceRef.current = null;
      return;
    }

    const visibleFor = loadingVisibleSinceRef.current == null
      ? loadingMinVisibleMs
      : Date.now() - loadingVisibleSinceRef.current;
    const remaining = Math.max(0, loadingMinVisibleMs - visibleFor);

    initialLoadingHideTimerRef.current = window.setTimeout(() => {
      setShowInitialLoadingIndicator(false);
      loadingVisibleSinceRef.current = null;
      initialLoadingHideTimerRef.current = null;
    }, remaining);
  }, [isInitialHistoryLoading, showInitialLoadingIndicator]);

  useEffect(() => {
    if (!activeSession || activeSession.historyState === 'loading') {
      return;
    }

    const pendingRestore = historyRestoreRef.current;
    const stage = messageStageRef.current;
    if (!pendingRestore || !stage || pendingRestore.sessionKey !== activeSession.sessionKey) {
      return;
    }

    const delta = stage.scrollHeight - pendingRestore.scrollHeight;
    stage.scrollTop = pendingRestore.scrollTop + delta;
    historyRestoreRef.current = null;
  }, [activeMessages.length, activeSession]);

  useEffect(() => {
    const timeline = messageTimelineRef.current;
    if (!timeline || typeof ResizeObserver === 'undefined') {
      return;
    }

    const observer = new ResizeObserver(() => {
      if (pendingInitialRevealRef.current || shouldStickToBottomRef.current) {
        scrollStageToBottom('auto');
      }
    });

    observer.observe(timeline);
    return () => observer.disconnect();
  }, [activeSession?.sessionKey]);

  useLayoutEffect(() => {
    if (!activeSession) {
      previousSessionKeyRef.current = null;
      previousLatestMessageRef.current = null;
      pendingInitialRevealRef.current = false;
      loadingVisibleSinceRef.current = null;
      latestUnseenMessageRef.current = null;
      setShowInitialLoadingIndicator(false);
      setShowJumpToLatest(false);
      setIsTimelineReady(true);
      return;
    }

    const stage = messageStageRef.current;
    if (!stage) {
      return;
    }

    const latestMessageMarker = activeMessages.length > 0
      ? activeMessages[activeMessages.length - 1].localId
      : null;
    const sessionChanged = previousSessionKeyRef.current !== activeSession.sessionKey;
    const latestChanged = previousLatestMessageRef.current !== latestMessageMarker;

    previousSessionKeyRef.current = activeSession.sessionKey;
    previousLatestMessageRef.current = latestMessageMarker;

    if (sessionChanged) {
      historyRestoreRef.current = null;
      pendingInitialRevealRef.current = true;
      shouldStickToBottomRef.current = true;
      latestUnseenMessageRef.current = null;
      loadingVisibleSinceRef.current = null;
      setShowInitialLoadingIndicator(false);
      setShowJumpToLatest(false);
      setIsTimelineReady(false);
    }

    if (latestChanged && !sessionChanged && !pendingInitialRevealRef.current) {
      if (shouldStickToBottomRef.current) {
        latestUnseenMessageRef.current = null;
        setShowJumpToLatest(false);
      } else {
        latestUnseenMessageRef.current = latestMessageMarker;
        setShowJumpToLatest(true);
      }
    }

    if (sessionChanged || (latestChanged && shouldStickToBottomRef.current) || pendingInitialRevealRef.current) {
      scrollStageToBottom('auto');
    }

    if (pendingInitialRevealRef.current && activeSession.historyState !== 'loading') {
      scheduleInitialReveal();
    }
  }, [activeMessages, activeSession]);

  useEffect(() => {
    const handleWindowBlur = () => {
      if (!audioPressingRef.current) {
        return;
      }
      audioPressingRef.current = false;
      void handleStopAudioRecording(true);
    };

    window.addEventListener('blur', handleWindowBlur);

    return () => {
      window.removeEventListener('blur', handleWindowBlur);
    };
  }, [handleStopAudioRecording]);

  const startAudioPress = () => {
    if (audioPressingRef.current) {
      return;
    }
    console.info('[voice] startAudioPress');
    audioPressingRef.current = true;
    void handleStartAudioRecording();
  };

  const finishAudioPress = () => {
    if (!audioPressingRef.current) {
      return;
    }
    console.info('[voice] finishAudioPress');
    audioPressingRef.current = false;
    void handleStopAudioRecording(false);
  };

  const cancelAudioPress = () => {
    if (!audioPressingRef.current) {
      return;
    }
    console.info('[voice] cancelAudioPress');
    audioPressingRef.current = false;
    void handleStopAudioRecording(true);
  };

  if (!activeSession) {
    return (
      <Center
        h="100%"
      >
      </Center>
    );
  }

  const imConnectionHint = connState === 'connecting'
    ? '聊天服务正在重新连接…'
    : connState === 'error'
      ? '聊天连接已断开，正在尝试重新连接…'
      : null;
  const showFileActions = activeSession.sessionType === 'single';

  // The header and empty state must resolve through the same canonical source.
  // A tiny map keeps the shared resolver type-safe for the already-selected
  // group without introducing a second avatar field into persisted sessions.
  const activeSessionAvatarUrl = resolveSessionAvatarUrl(
    activeSession,
    contactsById,
    activeGroup ? { [activeGroup.id]: activeGroup } : {},
  );

  return (
    <>
      <Box
        component="header"
        px={{ base: 8, sm: 'md' }}
        h={64}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          boxShadow: '0 1px 0 var(--locs-border)',
          backgroundColor: 'var(--locs-panel)',
          backdropFilter: 'blur(12px)',
          position: 'relative',
          zIndex: 10,
        }}
      >
        <ActionIcon
          variant="subtle"
          color="gray"
          onClick={onBackToList}
          hiddenFrom="sm"
          size="lg"
          radius="md"
        >
          <IconChevronLeft size={20} stroke={2.5} />
        </ActionIcon>

        {activeSession.sessionType === 'single' ? (
          <UnstyledButton
            onClick={() => onViewUserProfile?.(activeSession.sessionId)}
            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <UserAvatar
              id={activeSession.sessionId}
              name={activeSession.title}
              avatarUrl={activeSessionAvatarUrl}
              size={40}
              fallback={activeSession.avatarText}
              avatarKind="human"
            />
          </UnstyledButton>
        ) : (
          <UserAvatar
            id={activeSession.sessionId}
            name={activeSession.title}
            avatarUrl={activeSessionAvatarUrl}
            size={40}
            fallback={activeSession.avatarText}
            avatarKind="group"
          />
        )}

        <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
          {activeSession.sessionType === 'single' ? (
            <UnstyledButton
              onClick={() => onViewUserProfile?.(activeSession.sessionId)}
              style={{ textDecoration: 'none', color: 'inherit', textAlign: 'left', cursor: 'pointer' }}
            >
              <Text fw={800} size="md" truncate style={{ letterSpacing: '-0.01em' }}>
                {activeSession.title}
              </Text>
            </UnstyledButton>
          ) : (
            <Text fw={800} size="md" truncate style={{ letterSpacing: '-0.01em' }}>
              {activeSession.title}
            </Text>
          )}
          <Group gap={6} align="center" wrap="nowrap">
            {activeSession.sessionType === 'group' && (
              <Badge
                variant="light"
                color="terracotta"
                size="xs"
                radius="sm"
                px={4}
                h={16}
                fw={700}
                style={{ textTransform: 'none', flexShrink: 0 }}
              >
                Hub
              </Badge>
            )}
            {activeSession.sessionType === 'group' && (
              <Text size="xs" c="dimmed" truncate>
                {groupMemberSummary(activeGroup, contactsById, currentUserId)}
              </Text>
            )}
          </Group>
        </Stack>

        <Group gap={0} wrap="nowrap" style={{ flexShrink: 0 }}>
          {activeSession.sessionType === 'group' && (
            <Group gap={0} wrap="nowrap">
              <Tooltip label="语音房间" position="bottom" openDelay={500}>
                <ActionIcon
                  aria-label="语音房间"
                  title="语音房间"
                  variant="subtle"
                  color="gray"
                  size={32}
                  radius="md"
                  disabled={realtimeConnectionState !== 'connected' || !!activeRealtimeSession}
                  onClick={handleStartRealtimeAudioRoom}
                >
                  <IconPhoneCall size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="视频房间" position="bottom" openDelay={500}>
                <ActionIcon
                  aria-label="视频房间"
                  title="视频房间"
                  variant="subtle"
                  color="gray"
                  size={32}
                  radius="md"
                  disabled={realtimeConnectionState !== 'connected' || !!activeRealtimeSession}
                  onClick={handleStartRealtimeVideoRoom}
                >
                  <IconVideo size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="共享画板" position="bottom" openDelay={500}>
                <ActionIcon
                  aria-label="共享画板"
                  title="共享画板"
                  variant="subtle"
                  color="gray"
                  size={32}
                  radius="md"
                  disabled={realtimeConnectionState !== 'connected' || !!activeRealtimeSession}
                  onClick={handleStartRealtimeWhiteboard}
                >
                  <IconPalette size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Button
                variant="light"
                color="gray"
                size="xs"
                radius="md"
                visibleFrom="sm"
                onClick={handleOpenGroupDetail}
              >
                群组详情
              </Button>
            </Group>
          )}

          {activeSession.sessionType === 'single' && (
            <Group gap={0} wrap="nowrap">
              <Tooltip label="语音通话" position="bottom" openDelay={500}>
                <ActionIcon
                  aria-label="语音通话"
                  title="语音通话"
                  variant="subtle"
                  color="gray"
                  size={32}
                  radius="md"
                  disabled={!callBootstrap?.features.audioCall || callConnectionState !== 'connected' || !!activeCall}
                  onClick={() => handleStartCall('audio')}
                >
                  <IconPhoneCall size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="视频通话" position="bottom" openDelay={500}>
                <ActionIcon
                  aria-label="视频通话"
                  title="视频通话"
                  variant="subtle"
                  color="gray"
                  size={32}
                  radius="md"
                  disabled={!callBootstrap?.features.videoCall || callConnectionState !== 'connected' || !!activeCall}
                  onClick={() => handleStartCall('video')}
                >
                  <IconVideo size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
              <Tooltip label="共享画板" position="bottom" openDelay={500}>
                <ActionIcon
                  aria-label="共享画板"
                  title="共享画板"
                  variant="subtle"
                  color="gray"
                  size={32}
                  radius="md"
                  disabled={realtimeConnectionState !== 'connected' || !!activeRealtimeSession}
                  onClick={handleStartRealtimeWhiteboard}
                >
                  <IconPalette size={20} stroke={1.5} />
                </ActionIcon>
              </Tooltip>
            </Group>
          )}

          <Popover width={220} position="bottom-end" shadow="md" radius="md">
            <Popover.Target>
              <ActionIcon
                variant="subtle"
                color="gray"
                size={32}
                radius="md"
                data-testid="conversation-safety-menu"
              >
                <IconDots size={20} />
              </ActionIcon>
            </Popover.Target>
            <Popover.Dropdown p={6}>
              {activeSession.sessionType === 'single' ? (
                <Stack gap={2}>
                  <Button
                    fullWidth
                    justify="flex-start"
                    variant="subtle"
                    color="orange"
                    size="sm"
                    radius="md"
                    leftSection={<IconFlag size={16} />}
                    data-testid="conversation-report-user"
                    onClick={openReportDialog}
                  >
                    举报用户
                  </Button>
                  <Button
                    fullWidth
                    justify="flex-start"
                    variant="subtle"
                    color="red"
                    size="sm"
                    radius="md"
                    leftSection={<IconUserX size={16} />}
                    loading={blockingUserId === activeSession.sessionId}
                    data-testid="conversation-block-user"
                    onClick={() => void handleBlockUser(activeSession.sessionId, activeSession.title)}
                  >
                    屏蔽用户
                  </Button>
                </Stack>
              ) : (
                <Text size="xs" c="dimmed" px="xs" py={6}>群组安全功能将在后续补齐</Text>
              )}
            </Popover.Dropdown>
          </Popover>

          {/* 举报对话框 */}
          <Modal
            opened={reportOpen}
            onClose={() => setReportOpen(false)}
            title="举报用户"
            size="sm"
            radius="md"
            centered
          >
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                举报 <Text span fw={600} c="dark">{activeSession.title}</Text>，请选择举报原因：
              </Text>
              <RadioGroup
                value={reportReason}
                onChange={(v) => setReportReason(v as ReportReason)}
              >
                <Stack gap="xs">
                  {(Object.keys(REPORT_REASON_LABELS) as ReportReason[]).map((key) => (
                    <Radio key={key} value={key} label={REPORT_REASON_LABELS[key]} />
                  ))}
                </Stack>
              </RadioGroup>
              <Textarea
                label="补充说明（可选）"
                placeholder="请描述具体情况"
                value={reportDetail}
                onChange={(e) => setReportDetail(e.currentTarget.value)}
                maxLength={512}
                autosize
                minRows={2}
                maxRows={4}
              />
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={() => setReportOpen(false)} disabled={reportSubmitting}>
                  取消
                </Button>
                <Button
                  color="orange"
                  loading={reportSubmitting}
                  data-testid="conversation-report-submit"
                  onClick={() => void handleSubmitReport()}
                >
                  提交举报
                </Button>
              </Group>
            </Stack>
          </Modal>
        </Group>
        {realtimeFileNotice && (
          <Text size="xs" c="orange.8" role="status" mt={6}>
            {realtimeFileNotice}
          </Text>
        )}
      </Box>

      <Box flex={1} style={{ position: 'relative', minHeight: 0, overflow: 'hidden' }}>
        <Box
          component="section"
          ref={messageStageRef}
          h="100%"
          style={{
            overflowY: 'auto',
            position: 'relative',
            background:
              'radial-gradient(circle at 1px 1px, var(--locs-border-faint) 1px, transparent 0) 0 0 / 18px 18px, linear-gradient(180deg, var(--locs-canvas) 0%, var(--locs-canvas-muted) 100%)',
          }}
          onClick={dismissComposerPanels}
          onScroll={(event) => {
            if (!activeSession) {
              return;
            }

            const target = event.currentTarget;
            const distanceToBottom = target.scrollHeight - target.clientHeight - target.scrollTop;
            if (pendingInitialRevealRef.current) {
              shouldStickToBottomRef.current = true;
              latestUnseenMessageRef.current = null;
              setShowJumpToLatest(false);
              return;
            }

            const isAtBottom = distanceToBottom <= 48;
            shouldStickToBottomRef.current = isAtBottom;
            if (isAtBottom) {
              latestUnseenMessageRef.current = null;
              setShowJumpToLatest(false);
            } else if (latestUnseenMessageRef.current != null) {
              setShowJumpToLatest(true);
            }

            if (activeSession.historyState === 'loading') {
              return;
            }

            if (target.scrollTop > 48) {
              return;
            }

            const oldestMessage = activeMessages[0];
            if (!oldestMessage || oldestMessage.msgId <= 1) {
              return;
            }

            historyRestoreRef.current = {
              sessionKey: activeSession.sessionKey,
              scrollHeight: target.scrollHeight,
              scrollTop: target.scrollTop,
            };
            void loadHistory(activeSession.sessionKey, 'older');
          }}
        >
          {imConnectionHint && (
          <Box
            px="md"
            py={6}
            bg={connState === 'error' ? 'red.0' : 'var(--mantine-color-primary-light)'}
          >
            <Group gap="xs" align="center">
              <Indicator color={connState === 'error' ? 'red' : 'terracotta'} processing size={8} offset={0} />
              <Text size="xs" c={connState === 'error' ? 'red.9' : 'var(--mantine-color-primary-filled)'} fw={500}>
                {imConnectionHint}
              </Text>
            </Group>
          </Box>
        )}

        {isInitialHistoryLoading ? (
          showInitialLoadingIndicator ? (
            <InitialMessagesSkeleton />
          ) : (
            <Box h="100%" style={{ opacity: 0 }} />
          )
        ) : activeMessages.length === 0 ? (
          <Center h="100%" p="xl">
            <Stack align="center" gap="sm" ta="center">
              <Text size="sm" c="dimmed">
                {activeSession.historyError || '这里还没有消息，来聊点什么吧'}
              </Text>
              {activeSession.historyError && (
                <Button variant="light" onClick={() => void loadHistory(activeSession.sessionKey, 'initial')}>
                  重新加载
                </Button>
              )}
            </Stack>
          </Center>
        ) : (
          <Stack
            ref={messageTimelineRef}
            gap={12}
            py="md"
            style={{
              visibility: isTimelineReady ? 'visible' : 'hidden',
              minHeight: '100%',
            }}
          >
            {activeSession.historyError && (
              <Box px="md">
                <Badge color="red" variant="light" fullWidth radius="sm">{activeSession.historyError}</Badge>
              </Box>
            )}
            {activeMessages.map((message, index) => {
              const previous = index > 0 ? activeMessages[index - 1] : null;
              const showTimeSeparator = shouldShowChatTimeSeparator(message.ts, previous?.ts);

              const hasImageEnvelope = isImageMessageEnvelope(message.text);
              const imagePayload = extractImagePayload(message.text);
              const isImage = !!imagePayload;
              const isFile = !!message.fileMeta;
              const isCall = isCallMessageType(message.msgType);
              const isAudio = isAudioMsgType(message.msgType);
              const isOut = message.dir === 'out';
              const showNickname = shouldShowChatSenderNickname(activeSession.sessionType);

              return (
                <Box key={message.localId}>
                  {showTimeSeparator && (
                    <Center
                      py="xs"
                      px="md"
                      role="separator"
                      aria-label={`消息时间 ${formatChatTimelineTime(message.ts)}`}
                      data-testid={`chat-time-separator-${message.localId}`}
                    >
                      <Text size={rem(11)} c="dimmed" fw={600}>
                        {formatChatTimelineTime(message.ts)}
                      </Text>
                    </Center>
                  )}
                  <Group
                    wrap="nowrap"
                    align="flex-start"
                    gap="sm"
                    px="md"
                    justify={isOut ? 'flex-end' : 'flex-start'}
                  >
                    {!isOut && (
                      <Box w={34} flex="0 0 34px">
                        <UnstyledButton
                          onClick={() => onViewUserProfile?.(message.from)}
                          style={{ display: 'block', borderRadius: '50%', cursor: 'pointer' }}
                        >
                          <UserAvatar
                            id={message.from}
                            name={senderLabel(message, currentUserId, contactsById)}
                            avatarUrl={contactsById[message.from]?.avatarUrl}
                            size={34}
                            fallback={senderLabel(message, currentUserId, contactsById)}
                            avatarKind="human"
                          />
                        </UnstyledButton>
                      </Box>
                    )}

                    <Stack gap={4} align={isOut ? 'flex-end' : 'flex-start'} style={{ maxWidth: '85%', minWidth: 0 }}>
                      {showNickname && (
                        <Text
                          size="xs"
                          c="dimmed"
                          fw={600}
                          mx={2}
                          data-testid={`chat-sender-nickname-${message.localId}`}
                        >
                          {senderLabel(message, currentUserId, contactsById)}
                        </Text>
                      )}

                      <Group gap={6} wrap="nowrap" align="flex-end">
                        {isOut && message.status === 'pending' && (
                          <Loader
                            size={14}
                            color="gray"
                            role="status"
                            aria-label="发送中"
                            data-testid={`chat-message-sending-${message.localId}`}
                            style={{ flexShrink: 0, marginBottom: 4 }}
                          />
                        )}
                        <Paper
                          px={isImage || isAudio ? 0 : isCall ? 10 : 'sm'}
                          py={isImage || isAudio ? 0 : isCall ? 8 : 8}
                          radius={8}
                          bg={isFile ? 'var(--locs-panel)' : isOut ? 'terracotta.7' : 'var(--locs-panel)'}
                          c={isFile ? 'var(--locs-text)' : isOut ? 'white' : 'var(--locs-text)'}
                          shadow={isFile ? 'xs' : undefined}
                          withBorder={false}
                          data-testid={`chat-message-bubble-${message.localId}`}
                          data-message-direction={isOut ? 'outgoing' : 'incoming'}
                          style={{
                            overflow: 'hidden',
                            position: 'relative',
                            minWidth: isImage || isFile ? undefined : 40,
                            minHeight: isImage || isFile ? undefined : 40,
                            display: isImage || isFile ? undefined : 'flex',
                            alignItems: isImage || isFile ? undefined : 'center',
                          }}
                        >
                        {(() => {
                          if (message.fileMeta) {
                            const fileMeta = message.fileMeta;
                            const online = fileMeta.transport === 'online';
                            const transfer = online && fileMeta.transferId
                              ? transferById.get(fileMeta.transferId)
                              : undefined;
                            const lifecycle = online && fileMeta.transferId
                              ? fileTransferLifecycleById[fileMeta.transferId]
                              : undefined;
                            const canReceiveRealtime = online && message.dir === 'in' &&
                              lifecycle !== false && lifecycle?.state === 'offered' &&
                              (transfer == null || transfer.canAccept === true);
                            const statusLabel = online
                              ? realtimeFileTransferStatusLabel(lifecycle, message.dir)
                              : fileMessageStatusLabel(message);
                            return (
                              <Box
                                w={{ base: '100%', xs: 260 }}
                                p={4}
                                data-testid={`file-transfer-card-${fileMeta.fileId || fileMeta.assetId}`}
                                data-file-transfer-status={online ? (transfer?.status ?? 'unavailable') : 'persistent-asset'}
                              >
                                <Group wrap="nowrap" gap="sm">
                                  <Center
                                    w={40}
                                    h={40}
                                    bg="teal.1"
                                    c="teal.9"
                                    style={{ borderRadius: 10, flexShrink: 0 }}
                                  >
                                    <AppIcon icon={IconFileDescription} size={22} />
                                  </Center>
                                  <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                                    <Text size="sm" fw={750} truncate title={fileMeta.fileName} c="var(--locs-text)">
                                      {fileMeta.fileName}
                                    </Text>
                                    <Group gap={6} align="center">
                                      <Text size="xs" c="dimmed">
                                        {formatFileBytes(fileMeta.fileSize)}
                                      </Text>
                                      <Badge size="xs" variant="light" color={online ? 'indigo' : 'teal'}>
                                        {online ? '实时传输' : '离线文件'}
                                      </Badge>
                                    </Group>
                                  </Stack>
                                </Group>
                                
                                {statusLabel && (
                                  <Text size="10px" mt="xs" c="dimmed">
                                    {statusLabel}
                                  </Text>
                                )}

                                {message.dir === 'in' && !online && fileMeta.assetId && (
                                  <Button
                                    data-testid={`file-asset-download-${message.fileMeta.assetId}`}
                                    fullWidth
                                    mt="xs"
                                    variant="light"
                                    color="orange"
                                    size="compact-xs"
                                    onClick={() => void handleAcceptFileTask(
                                      fileMeta.assetId,
                                      fileMeta.fileName,
                                    )}
                                  >
                                    下载
                                  </Button>
                                )}
                                {canReceiveRealtime && fileMeta.transferId && (
                                  <Button
                                    data-testid={`file-realtime-accept-${fileMeta.transferId}`}
                                    fullWidth
                                    mt="xs"
                                    variant="light"
                                    color="orange"
                                    size="compact-xs"
                                    onClick={() => void handleAcceptRealtimeFile(fileMeta.transferId!, {
                                      fileName: fileMeta.fileName,
                                      fileSize: fileMeta.fileSize,
                                      sha256: fileMeta.sha256,
                                    })}
                                  >接收</Button>
                                )}

                              </Box>
                            );
                          }
                          if (isAudioMsgType(message.msgType)) {
                            return (
                              <AudioMessageBubble
                                msgData={message.msgData}
                                isMine={isOut}
                              />
                            );
                          }
                          if (isCallMessageType(message.msgType)) {
                            return <CallMessageCard message={message} isMine={isOut} />;
                          }
                          if (imagePayload) {
                            const imageUrl = resolveImageUrl(imagePayload, cdnBase);
                            const imageStyle = {
                              width: imageBubbleWidth(imagePayload.meta),
                              ...(imageAspectRatio(imagePayload.meta)
                                ? { aspectRatio: imageAspectRatio(imagePayload.meta) }
                                : {}),
                            };
                            return (
                              <UnstyledButton
                                component="div"
                                style={{ ...imageStyle, position: 'relative', display: 'block' }}
                                onClick={() => setPreviewImageUrl(imageUrl)}
                              >
                                <img
                                  src={imageUrl}
                                  alt="图片消息"
                                  loading="lazy"
                                  onLoad={() => {
                                    if (pendingInitialRevealRef.current || shouldStickToBottomRef.current) {
                                      window.requestAnimationFrame(() => scrollStageToBottom('auto'));
                                    }
                                  }}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                />
                                {(message.status === 'pending' || message.status === 'failed') && (
                                  <Center
                                    style={{
                                      position: 'absolute',
                                      inset: 0,
                                      backgroundColor: 'rgba(0,0,0,0.4)',
                                      color: 'white',
                                    }}
                                  >
                                    {message.status === 'pending' ? <Loader size="xs" color="gray" /> : <Text size="xs">发送失败</Text>}
                                  </Center>
                                )}
                              </UnstyledButton>
                            );
                          }
                          if (hasImageEnvelope) {
                            return <Text size="sm">图片消息无效</Text>;
                          }
                          return (
                            <Text size="sm" c={isOut ? 'white' : 'var(--locs-text)'} style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                              <EmojiText text={message.text} />
                            </Text>
                          );
                        })()}
                        </Paper>
                      </Group>

                      {isOut && message.status === 'failed' && (
                        <Group gap={6} px={4}>
                          <Badge
                            size="xs"
                            variant="transparent"
                            p={0}
                            color="red"
                          >
                            失败
                          </Badge>
                        </Group>
                      )}
                    </Stack>

                    {isOut && showNickname && (
                      <Box w={34} flex="0 0 34px">
                        <UnstyledButton
                          onClick={() => onViewUserProfile?.(currentUserId)}
                          style={{ display: 'block', borderRadius: '50%', cursor: 'pointer' }}
                        >
                          <UserAvatar
                            id={currentUserId}
                            name="我"
                            size={34}
                            fallback="我"
                            avatarKind="human"
                          />
                        </UnstyledButton>
                      </Box>
                    )}
                  </Group>
                </Box>
              );
            })}
            <div ref={feedEnd} />
          </Stack>
        )}

        </Box>

        {showJumpToLatest && (
          <Button
            variant="white"
            radius="md"
            size="xs"
            leftSection={<IconArrowDown size={14} />}
            onClick={() => scrollStageToBottom('smooth')}
            style={{
              position: 'absolute',
              bottom: 16,
              right: 16,
              zIndex: 20,
              border: '1px solid var(--mantine-color-gray-2)',
              boxShadow: '0 10px 24px rgba(42, 31, 24, 0.18)',
            }}
          >
            最新消息
          </Button>
        )}
      </Box>

      {/* Desktop Footer (PC Layout - exactly original) */}
      <Box
        component="footer"
        px="md"
        pt="xs"
        pb="md"
        visibleFrom="sm"
        style={{
          boxShadow: '0 -1px 0 var(--locs-border)',
          backgroundColor: 'var(--locs-panel)',
          backdropFilter: 'blur(16px)',
          zIndex: 10,
          position: 'relative',
        }}
      >
        <Group align="center" gap="xs" wrap="nowrap">
          <Paper
            withBorder={false}
            p={4}
            radius={12}
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              backgroundColor: 'var(--locs-panel)',
              minHeight: 54,
              transition: 'background-color 0.2s ease',
              position: 'relative',
              border: '1px solid var(--control-border)',
              boxShadow: 'none',
            }}
          >
            {isAudioMode ? (
              <UnstyledButton
                ref={audioTriggerRef}
                style={{
                  flex: 1,
                  height: 50,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '0 26px',
                  borderRadius: 12,
                  backgroundColor: isRecordingAudio ? 'var(--mantine-color-terracotta-light)' : 'var(--locs-panel)',
                  transition: 'all 0.2s ease',
                  touchAction: 'none',
                }}
                disabled={!isOnline || !activeSession}
                onPointerDown={(event) => {
                  event.preventDefault();
                  audioTriggerRef.current?.setPointerCapture?.(event.pointerId);
                  startAudioPress();
                }}
                onPointerUp={(event) => {
                  event.preventDefault();
                  if (audioTriggerRef.current?.hasPointerCapture?.(event.pointerId)) {
                    audioTriggerRef.current.releasePointerCapture(event.pointerId);
                  }
                  finishAudioPress();
                }}
                onPointerCancel={(event) => {
                  event.preventDefault();
                  if (audioTriggerRef.current?.hasPointerCapture?.(event.pointerId)) {
                    audioTriggerRef.current.releasePointerCapture(event.pointerId);
                  }
                  cancelAudioPress();
                }}
                onLostPointerCapture={(event) => {
                  if (!audioPressingRef.current) {
                    return;
                  }
                  event.preventDefault();
                  finishAudioPress();
                }}
                onContextMenu={(event) => {
                  event.preventDefault();
                }}
              >
                <Group gap={10} wrap="nowrap" align="center">
                  {isRecordingAudio ? (
                    <>
                      <Box w={9} h={9} bg="var(--mantine-color-red-6)" style={{ borderRadius: '50%' }} />
                      <Text size="sm" fw={700} c="var(--locs-text)">{recordingLabel}</Text>
                    </>
                  ) : (
                    <>
                      <AppIcon icon={IconMicrophone} size={20} color="var(--mantine-color-terracotta-filled)" />
                      <Text size="sm" fw={700} c="var(--locs-text)">按住说话</Text>
                    </>
                  )}
                </Group>
                <Group gap={4} wrap="nowrap" align="center">
                  {(isRecordingAudio ? recordingLevels : [0.32, 0.52, 0.72]).map((level, index) => (
                    <Box
                      key={`recording-level-${index}`}
                      w={4}
                      bg="var(--mantine-color-terracotta-filled)"
                      style={{
                        height: isRecordingAudio ? 20 : 10 + index * 5,
                        transform: isRecordingAudio ? `scaleY(${0.2 + level * 0.8})` : undefined,
                        opacity: isRecordingAudio ? 1 : 0.28,
                        borderRadius: 999,
                        transition: 'transform 0.1s ease',
                      }}
                    />
                  ))}
                </Group>
              </UnstyledButton>
            ) : (
              <EmojiComposer
                ref={composerRef}
                value={composerText}
                onChange={setComposerText}
            onKeyDown={handleComposerInputKeyDown}
                placeholder={!activeSession ? '选择一个对话开始聊天' : !isOnline ? '等待连接' : '输入消息'}
                disabled={!isOnline || !activeSession}
              />
            )}

            <Group gap={2} px={4} wrap="nowrap" style={{ flexShrink: 0 }}>
              <ActionIcon
                variant="subtle"
                color={isAudioMode ? 'terracotta' : 'gray'}
                radius="md"
                size="lg"
                onClick={() => setIsAudioMode((prev) => !prev)}
                title={isAudioMode ? '切换到文字输入' : '切换到语音输入'}
                disabled={!activeSession}
              >
                <AppIcon icon={isAudioMode ? IconKeyboard : IconMicrophone} size={18} />
              </ActionIcon>

              <ImageSourcePicker
                disabled={!isOnline || !activeSession}
                onSelect={handleChatImageSelect}
                renderTrigger={({ open, openState }) => (
                  <ActionIcon
                    variant={openState ? 'light' : 'subtle'}
                    color="gray"
                    radius="md"
                    size="lg"
                    onClick={open}
                    title="发送图片"
                    disabled={!isOnline || !activeSession}
                  >
                    <AppIcon icon={IconPhoto} size={18} />
                  </ActionIcon>
                )}
              />

              {showFileActions && (
                <>
                  <Tooltip label="发送聊天附件" openDelay={500}>
                    <ActionIcon
                      component="label"
                      data-testid="chat-send-file"
                      aria-label="发送聊天附件"
                      htmlFor="chat-attachment-file-input"
                      variant="subtle"
                      color="gray"
                      radius="md"
                      size="lg"
                      disabled={!isOnline || !activeSession}
                    >
                      <AppIcon icon={IconPaperclip} size={18} />
                    </ActionIcon>
                  </Tooltip>
                  {fileOperationCapability.realtimeAvailable && (
                  <Tooltip label="实时传输" openDelay={500}>
                    <ActionIcon
                      component="label"
                      data-testid="chat-send-realtime-file"
                      aria-label="实时传输文件"
                      htmlFor="chat-realtime-file-input"
                      variant="subtle"
                      color="indigo"
                      radius="md"
                      size="lg"
                      disabled={realtimeFileStarting}
                    >
                      <AppIcon icon={IconFileDescription} size={18} />
                    </ActionIcon>
                  </Tooltip>
                  )}
                </>
              )}

              <Popover
                opened={isEmojiPickerOpen && !isMobileViewport}
                onChange={setIsEmojiPickerOpen}
                position="top-end"
                offset={8}
                shadow="md"
                radius="lg"
                withArrow={false}
                withinPortal
              >
                <Popover.Target>
                  <ActionIcon
                    variant={isEmojiPickerOpen ? 'light' : 'subtle'}
                    color="gray"
                    radius="md"
                    size="lg"
                    onClick={() => setIsEmojiPickerOpen((prev) => !prev)}
                    title="选择表情"
                    ref={emojiToggleRef}
                    disabled={isAudioMode}
                  >
                    <AppIcon icon={IconMoodSmile} size={18} />
                  </ActionIcon>
                </Popover.Target>
                <Popover.Dropdown
                  p={0}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    boxShadow: 'none',
                    width: 'min(360px, calc(100vw - 24px))',
                    overflow: 'visible',
                  }}
                >
                  <EmojiPicker
                    onSelect={(placeholder) => {
                      composerRef.current?.insertEmoji(placeholder);
                      window.requestAnimationFrame(() => composerRef.current?.focus());
                    }}
                  />
                </Popover.Dropdown>
              </Popover>
            </Group>
          </Paper>

          <ActionIcon
            variant="filled"
            color="terracotta"
            size={54}
            radius="sm"
            onClick={submitComposerMessage}
            disabled={!isOnline || !activeSession || isAudioMode}
            title="发送消息"
            aria-label="发送消息"
            style={{ flexShrink: 0 }}
          >
            <AppIcon icon={IconSend2} size={24} />
          </ActionIcon>
        </Group>
      </Box>

      {/* Mobile Footer (Flutter Aligned Layout) */}
      <Box
        component="footer"
        px="sm"
        pt="xs"
        pb="sm"
        hiddenFrom="sm"
        style={{
          boxShadow: '0 -1px 0 var(--locs-border)',
          backgroundColor: 'var(--locs-panel)',
          backdropFilter: 'blur(16px)',
          zIndex: 10,
          position: 'relative',
        }}
      >
        <Stack gap="xs">
          <Group align="center" gap="xs" wrap="nowrap">
            {/* Left Mic button */}
            <ActionIcon
              variant="subtle"
              color={isAudioMode ? 'terracotta' : 'gray'}
              radius="md"
              size={40}
              onClick={() => {
                setIsAudioMode((prev) => !prev);
                setIsToolsPanelOpen(false);
                setIsEmojiPickerOpen(false);
              }}
              title={isAudioMode ? '切换到文字输入' : '切换到语音输入'}
              disabled={!activeSession}
              style={{ flexShrink: 0 }}
            >
              <AppIcon icon={isAudioMode ? IconKeyboard : IconMicrophone} size={20} />
            </ActionIcon>

            {/* Middle Paper Box containing input + plus + smile */}
            <Paper
              withBorder={false}
              p={2}
              radius={12}
              style={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                backgroundColor: 'var(--locs-panel)',
                minHeight: 50,
                transition: 'background-color 0.2s ease',
                position: 'relative',
                border: '1px solid var(--control-border)',
                boxShadow: 'none',
              }}
            >
              {isAudioMode ? (
                <UnstyledButton
                  ref={audioTriggerRef}
                  style={{
                    flex: 1,
                    height: 46,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '0 30px',
                    borderRadius: 12,
                    backgroundColor: isRecordingAudio ? 'var(--mantine-color-terracotta-light)' : 'var(--locs-panel)',
                    transition: 'all 0.2s ease',
                    touchAction: 'none',
                  }}
                  disabled={!isOnline || !activeSession}
                  onPointerDown={(event) => {
                    event.preventDefault();
                    audioTriggerRef.current?.setPointerCapture?.(event.pointerId);
                    startAudioPress();
                  }}
                  onPointerUp={(event) => {
                    event.preventDefault();
                    if (audioTriggerRef.current?.hasPointerCapture?.(event.pointerId)) {
                      audioTriggerRef.current.releasePointerCapture(event.pointerId);
                    }
                    finishAudioPress();
                  }}
                  onPointerCancel={(event) => {
                    event.preventDefault();
                    if (audioTriggerRef.current?.hasPointerCapture?.(event.pointerId)) {
                      audioTriggerRef.current.releasePointerCapture(event.pointerId);
                    }
                    cancelAudioPress();
                  }}
                  onLostPointerCapture={(event) => {
                    if (!audioPressingRef.current) {
                      return;
                    }
                    event.preventDefault();
                    finishAudioPress();
                  }}
                  onContextMenu={(event) => {
                    event.preventDefault();
                  }}
                >
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
                    {isRecordingAudio ? (
                      <>
                        <Box w={9} h={9} bg="var(--mantine-color-red-6)" style={{ borderRadius: '50%' }} />
                        <Text size="sm" fw={700} c="var(--locs-text)">{recordingLabel}</Text>
                      </>
                    ) : (
                      <>
                        <IconMicrophone
                          size={20}
                          stroke={2}
                          color="var(--mantine-color-terracotta-filled)"
                          style={{ display: 'block', flexShrink: 0 }}
                          aria-hidden="true"
                        />
                        <Text size="sm" fw={700} c="var(--locs-text)">按住说话</Text>
                      </>
                    )}
                  </Box>
                  <Box style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }} aria-hidden="true">
                    {(isRecordingAudio ? recordingLevels : [0.32, 0.52, 0.72]).map((level, index) => (
                      <Box
                        key={`recording-level-${index}`}
                        style={{
                          width: 4,
                          height: isRecordingAudio ? 20 : 10 + index * 5,
                          backgroundColor: 'var(--mantine-color-terracotta-filled)',
                          transform: isRecordingAudio ? `scaleY(${0.2 + level * 0.8})` : undefined,
                          opacity: isRecordingAudio ? 1 : 0.28,
                          borderRadius: 999,
                          flexShrink: 0,
                          transition: 'transform 0.1s ease',
                        }}
                      />
                    ))}
                  </Box>
                </UnstyledButton>
              ) : (
                <EmojiComposer
                  ref={composerRef}
                  value={composerText}
                  onChange={setComposerText}
                  onKeyDown={handleComposerInputKeyDown}
                  placeholder={!activeSession ? '选择一个对话开始聊天' : !isOnline ? '等待连接' : '输入消息'}
                  disabled={!isOnline || !activeSession}
                />
              )}

              {/* Plus & Smile icons inside input box on mobile */}
              {!isAudioMode && (
                <Group gap={2} px={4} wrap="nowrap" align="center" style={{ flexShrink: 0 }}>
                  <ActionIcon
                    variant="subtle"
                    color={isToolsPanelOpen ? 'terracotta' : 'gray'}
                    radius="xl"
                    size="md"
                    onClick={() => {
                      setIsToolsPanelOpen((prev) => !prev);
                      setIsEmojiPickerOpen(false);
                    }}
                    title="更多功能"
                    disabled={!activeSession}
                  >
                    <AppIcon icon={IconPlus} size={18} />
                  </ActionIcon>

                  <ActionIcon
                    variant="subtle"
                    color={isEmojiPickerOpen ? 'terracotta' : 'gray'}
                    radius="xl"
                    size="md"
                    onClick={() => {
                      setIsEmojiPickerOpen((prev) => !prev);
                      setIsToolsPanelOpen(false);
                    }}
                    title="选择表情"
                    disabled={!activeSession}
                  >
                    <AppIcon icon={IconMoodSmile} size={18} />
                  </ActionIcon>
                </Group>
              )}
            </Paper>

            {!isAudioMode && (
              <ActionIcon
                variant="filled"
                color="terracotta"
                size={40}
                radius="xl"
                onClick={submitComposerMessage}
                disabled={!isOnline || !activeSession}
                title="发送消息"
                aria-label="发送消息"
                style={{ flexShrink: 0 }}
              >
                <AppIcon icon={IconSend2} size={20} />
              </ActionIcon>
            )}
          </Group>

          {/* Expanded panels */}
          {isToolsPanelOpen && (
            <Box py="xs" style={{ borderTop: '1px solid var(--locs-border-faint)' }}>
              <Group justify="space-around" align="center" gap="lg">
                <Stack gap={4} align="center">
                  <ImageSourcePicker
                    disabled={!isOnline || !activeSession}
                    onSelect={handleChatImageSelect}
                    renderTrigger={({ open }) => (
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        radius="md"
                        size={50}
                        onClick={open}
                        title="发送图片"
                        disabled={!isOnline || !activeSession}
                      >
                        <AppIcon icon={IconPhoto} size={24} />
                      </ActionIcon>
                    )}
                  />
                  <Text size="xs" c="dimmed">图片</Text>
                </Stack>

                {showFileActions && (
                  <>
                    <Stack gap={4} align="center">
                      <ActionIcon
                        component="label"
                        data-testid="chat-send-file-mobile"
                        aria-label="发送聊天附件"
                        htmlFor="chat-attachment-file-input"
                        variant="subtle"
                        color="gray"
                        radius="md"
                        size={50}
                        disabled={!isOnline || !activeSession}
                      >
                        <AppIcon icon={IconPaperclip} size={24} />
                      </ActionIcon>
                      <Text size="xs" c="dimmed">聊天附件</Text>
                    </Stack>

                    {fileOperationCapability.realtimeAvailable && (
                    <Stack gap={4} align="center">
                      <ActionIcon
                        component="label"
                        data-testid="chat-send-realtime-file-mobile"
                        aria-label="实时传输文件"
                        htmlFor="chat-realtime-file-input"
                        variant="subtle"
                        color="indigo"
                        radius="md"
                        size={50}
                        disabled={realtimeFileStarting}
                      >
                        <AppIcon icon={IconFileDescription} size={24} />
                      </ActionIcon>
                      <Text size="xs" c="dimmed">实时传输</Text>
                    </Stack>
                    )}

                  </>
                )}
              </Group>
            </Box>
          )}

          {realtimeFileNotice && (
            <Text size="xs" c="orange.8" role="status" px="sm" pb={6}>
              {realtimeFileNotice}
            </Text>
          )}

          {isEmojiPickerOpen && (
            <Box py="xs" style={{ borderTop: '1px solid var(--locs-border-faint)' }}>
              <EmojiPicker
                variant="inline"
                onSelect={(placeholder) => {
                  composerRef.current?.insertEmoji(placeholder);
                  window.requestAnimationFrame(() => composerRef.current?.focus());
                }}
              />
            </Box>
          )}
        </Stack>
      </Box>

      {/* Hidden file inputs shared by both footers */}
      <input
        id="chat-attachment-file-input"
        style={{ display: 'none' }}
        type="file"
        disabled={!isOnline || !activeSession}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.currentTarget.value = '';
          if (!file) {
            return;
          }
          void handleChatFileSelect(file);
        }}
      />
      <input
        id="chat-realtime-file-input"
        style={{ display: 'none' }}
        type="file"
        disabled={!isOnline || !activeSession}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.currentTarget.value = '';
          if (file) void handleRealtimeFileSelect(file);
        }}
      />
    </>
  );
}
